/**
 * O "Conferir agora" da tela das saídas (E170.F2.C05, GUS-229, reteste 3 de 25/10): no GT-2026-0031
 * o botão era recusado com a conclusão da CCB pendente. Ele aparece só com a conferência da saída
 * liberada na prontidão do servidor (`conferencia_da_saida`); antes, a tela diz o primeiro passo que
 * a segura e onde ele se faz, no padrão do próximo passo. Sem a prontidão lida agora, o botão fica e
 * o servidor decide.
 */
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { caminhoDaTela, telaDoPasso } from '@/lib/proximoPasso';
import type { PendenciaDaProntidaoData } from '@/lib/resultadoAuditado';
import type { EstadoDoResultadoNaTela } from '@/lib/useResultadoDoCaso';

const t = ptBR.conferenciaDasSaidas;

/** O passo que segura a conferência; `null` quando ela pode rodar ou a prontidão não foi lida;
 * `undefined` enquanto carrega. */
export function impedimentoDaConferencia(
  resultado: EstadoDoResultadoNaTela,
): PendenciaDaProntidaoData | null | undefined {
  if (resultado.estado === 'carregando') return undefined;
  if (resultado.estado === 'erro') return null;
  const prontidao = resultado.dados.prontidao;
  if (prontidao === null || prontidao.conferencia_da_saida.liberada) return null;
  const { motivo } = prontidao.conferencia_da_saida;
  return prontidao.pendencias.find((p) => p.motivo === motivo) ?? prontidao.pendencias[0] ?? null;
}

export interface ConferirAgoraProps {
  caseId: string;
  resultado: EstadoDoResultadoNaTela;
  conferindo: boolean;
  onConferir: () => void;
}

export function ConferirAgora({ caseId, resultado, conferindo, onConferir }: ConferirAgoraProps) {
  const segura = impedimentoDaConferencia(resultado);
  if (segura === undefined) return null;
  if (segura !== null) {
    const tela = telaDoPasso(segura.passo);
    return (
      <p className="max-w-prose text-body-sm text-fg-muted" data-testid="conferir-agora-impedido">
        {t.conferirDepois.replace('{acao}', ptBR.resultadoAuditado.proximoPasso[segura.passo])}{' '}
        <Link className="text-accent-700 underline" to={caminhoDaTela(tela, caseId)}>
          {ptBR.resultadoAuditado.prontidao.abrir[tela]}
        </Link>
      </p>
    );
  }
  return (
    <div className="space-y-sc-1">
      <Button
        className="self-start"
        disabled={conferindo}
        onClick={onConferir}
        data-testid="conferir-agora"
      >
        {conferindo ? t.conferindo : t.conferirAgora}
      </Button>
      <p className="max-w-prose text-caption text-fg-muted">{t.conferirAgoraAjuda}</p>
    </div>
  );
}
